import React from "react";
import {
  Redirect,
  Route,
  BrowserRouter as Router,
  Switch,
} from "react-router-dom";

import HealthCenters from "./HealthCenters";
import InsurancePolicies from "./InsurancePolicies";
import Specialities from "./Specialities";
import CallsAll from "./CallsAll";
import ResetPassword from "./ResetPassword";
import MyPatientsPage from "./MyPatients";
import Admins from "./Admins";
import Authors from "./Authors";
import Articles from "./Articles";
import Advertisements from "./Advertisements";
import CallPage from "./Call";
import Cards from "./Cards";
import Subscription from "./Subscription";
import MyAccount from "./MyAccount";
import Faq from "./Faq";
import CallDetails from "./CallDetails";
import SubscriptionsNeedReview from "./SubscriptionsNeedReview";
import CallsReport from "./CallsReport";
import CallsWaitingQueue from "./CallsWaitingQueue";
import Cities from "./Cities";
import Countries from "./Countries";
import CreateMedicalCard from "./CreateMedicalCard";
import DoctorRequest from "./DoctorRequest";
import DoctorRequests from "./DoctorRequests";
import Doctors from "./Doctors";
import EventPage from "./Event";
import EventsPage from "./Events";
import LandingPage from "./LandingPage";
import LoginPage from "./Login";
import MedicalCard from "./MedicalCard";

import { MedicalCardDocument } from "./MedicalCard/document";
import MyEventsPage from "./MyEvents";
import PatientPage from "./Patient";
import PatientsPage from "./Patients";
import RegisterDoctor from "./RegisterDoctor";
import SendNotification from "./SendNotification";
import Specialists from "./Specialists";
import Stories from "./Stories";
import StoryItems from "./Stories/item";
import Subscriptions from "./Subscriptions";
import SubscriptionsReport from "./SubscriptionsReport";
import Tier from "./Tier";
import TierList from "./TierList";
import VerifyAccountPage from "./VerifyAccount";
import { Loader } from "../components/Loader";
import { Private } from "../components/Private";
import { AccountRole, useMeQuery } from "../graphql";
import DashboardLayout from "../layouts/Dashboard";
import Benefit from "../graphql/queries/benefit";
import Benefits from "./Benefits";
import { BlogArticle } from "./BlogArticle";
import MedicalConcernCategories from "./MedicalConcerns";
import CreateArticlePage from "./Articles/create";
import ArticleEditPage from "./Articles/edit";
import MedicalCardsByMedicalConcernReport from "./MedicalCardsByMedicalConcernsReport";
import MediaLibrary from "./MediaLibrary";
import CallsByDoctorReport from "./CallsByDoctorReport";

const Index = () => {
  const { data, loading } = useMeQuery();

  if (loading) {
    return <Loader />;
  }

  if (data.me.role === AccountRole.Admin) {
    return <Redirect to="/calls/overview" />;
  }

  if (data.me.role === AccountRole.Author) {
    return <Redirect to="/articles" />;
  }

  if (
    data.me.role === AccountRole.Doctor ||
    data.me.role === AccountRole.MedicalSpecialist
  ) {
    return <Redirect to="/calls/my" />;
  }
};

export default function Routes() {
  return (
    <Router>
      <Switch>
        <Route path="/login" exact>
          <LoginPage />
        </Route>

        <Route path="/verify-account/:code" exact>
          <VerifyAccountPage />
        </Route>

        <Route path="/reset-password" exact>
          <ResetPassword />
        </Route>

        <Route path="/register" exact>
          <RegisterDoctor />
        </Route>

        <Route path="/" exact>
          <LandingPage />
        </Route>

        <Route path="/blog/article/:id" exact>
          <BlogArticle />
        </Route>

        <DashboardLayout>
          <Route path="/home" exact>
            <Private
              roles={[
                AccountRole.Admin,
                AccountRole.Author,
                AccountRole.Doctor,
                AccountRole.MedicalSpecialist,
              ]}
            >
              <Index />
            </Private>
          </Route>
          <Route path="/benefits" exact>
            <Private roles={[AccountRole.Admin, AccountRole.Author]}>
              <Benefits />
            </Private>
          </Route>
          <Route path="/stories" exact>
            <Private roles={[AccountRole.Admin, AccountRole.Author]}>
              <Stories />
            </Private>
          </Route>
          <Route path="/story/:id" exact>
            <Private roles={[AccountRole.Admin, AccountRole.Author]}>
              <StoryItems />
            </Private>
          </Route>
          <Route path="/calls/my" exact>
            <Private
              roles={[AccountRole.Doctor, AccountRole.MedicalSpecialist]}
            >
              <CallPage />
            </Private>
          </Route>
          <Route path="/call/:id" exact>
            <Private roles={[AccountRole.Admin]}>
              <CallDetails />
            </Private>
          </Route>
          <Route path="/calls/overview" exact>
            <Private roles={[AccountRole.Admin]}>
              <CallsWaitingQueue />
            </Private>
          </Route>
          <Route path="/report/medical-cards-by-concern" exact>
            <Private roles={[AccountRole.Admin]}>
              <MedicalCardsByMedicalConcernReport />
            </Private>
          </Route>
          <Route path="/report/calls" exact>
            <Private roles={[AccountRole.Admin]}>
              <CallsReport />
            </Private>
          </Route>
          <Route path="/report/calls-by-doctor" exact>
            <Private roles={[AccountRole.Admin]}>
              <CallsByDoctorReport />
            </Private>
          </Route>
          <Route path="/report/subscriptions" exact>
            <Private roles={[AccountRole.Admin]}>
              <SubscriptionsReport />
            </Private>
          </Route>
          <Route path="/notify-all" exact>
            <Private roles={[AccountRole.Admin]}>
              <SendNotification />
            </Private>
          </Route>
          <Route path="/calls/all" exact>
            <Private roles={[AccountRole.Admin]}>
              <CallsAll />
            </Private>
          </Route>
          <Route path="/account/create-medical-card" exact>
            <Private
              roles={[
                AccountRole.Admin,
                AccountRole.Doctor,
                AccountRole.MedicalSpecialist,
              ]}
            >
              <CreateMedicalCard />
            </Private>
          </Route>
          <Route path="/account/:clientId/visit/:visitId" exact>
            <Private
              roles={[
                AccountRole.Admin,
                AccountRole.Doctor,
                AccountRole.MedicalSpecialist,
              ]}
            >
              <MedicalCard />
            </Private>
          </Route>
          <Route
            path="/account/:clientId/visit/:visitId/document/:documentId"
            exact
          >
            <Private
              roles={[
                AccountRole.Admin,
                AccountRole.Doctor,
                AccountRole.MedicalSpecialist,
              ]}
            >
              <MedicalCardDocument />
            </Private>
          </Route>
          <Route path="/account/:clientId" exact>
            <Private
              roles={[
                AccountRole.Admin,
                AccountRole.Doctor,
                AccountRole.MedicalSpecialist,
              ]}
            >
              <PatientPage />
            </Private>
          </Route>
          <Route path="/clients" exact>
            <Private roles={[AccountRole.Admin]}>
              <PatientsPage />
            </Private>
          </Route>
          <Route path="/patients" exact>
            <Private
              roles={[AccountRole.Doctor, AccountRole.MedicalSpecialist]}
            >
              <MyPatientsPage />
            </Private>
          </Route>
          <Route path="/doctors" exact>
            <Private roles={[AccountRole.Admin]}>
              <Doctors />
            </Private>
          </Route>
          <Route path="/specialists" exact>
            <Private roles={[AccountRole.Admin]}>
              <Specialists />
            </Private>
          </Route>
          <Route path="/admins" exact>
            <Private roles={[AccountRole.Admin]}>
              <Admins />
            </Private>
          </Route>
          <Route path="/authors" exact>
            <Private roles={[AccountRole.Admin]}>
              <Authors />
            </Private>
          </Route>
          <Route path="/articles" exact>
            <Private roles={[AccountRole.Admin, AccountRole.Author]}>
              <Articles />
            </Private>
          </Route>
          <Route path="/ads" exact>
            <Private roles={[AccountRole.Admin, AccountRole.Author]}>
              <Advertisements />
            </Private>
          </Route>
          <Route path="/media-library" exact>
            <Private roles={[AccountRole.Admin, AccountRole.Author]}>
              <MediaLibrary />
            </Private>
          </Route>
          <Route path="/subscriptions" exact>
            <Private roles={[AccountRole.Admin]}>
              <Subscriptions />
            </Private>
          </Route>
          <Route path="/subscriptions/need-review" exact>
            <Private roles={[AccountRole.Admin]}>
              <SubscriptionsNeedReview />
            </Private>
          </Route>
          <Route path="/subscription/:id" exact>
            <Private roles={[AccountRole.Admin]}>
              <Subscription />
            </Private>
          </Route>
          <Route path="/my-account" exact>
            <Private
              roles={[
                AccountRole.Admin,
                AccountRole.Doctor,
                AccountRole.MedicalSpecialist,
                AccountRole.Author,
              ]}
            >
              <MyAccount />
            </Private>
          </Route>
          <Route path="/faqs" exact>
            <Private roles={[AccountRole.Admin, AccountRole.Author]}>
              <Faq />
            </Private>
          </Route>
          <Route path="/countries" exact>
            <Private roles={[AccountRole.Admin]}>
              <Countries />
            </Private>
          </Route>
          <Route path="/cities" exact>
            <Private roles={[AccountRole.Admin]}>
              <Cities />
            </Private>
          </Route>
          <Route path="/health-centers" exact>
            <Private roles={[AccountRole.Admin]}>
              <HealthCenters />
            </Private>
          </Route>
          <Route path="/insurance-policies" exact>
            <Private roles={[AccountRole.Admin]}>
              <InsurancePolicies />
            </Private>
          </Route>
          <Route path="/specialities" exact>
            <Private roles={[AccountRole.Admin]}>
              <Specialities />
            </Private>
          </Route>
          <Route path="/medical-concerns" exact>
            <Private roles={[AccountRole.Admin]}>
              <MedicalConcernCategories />
            </Private>
          </Route>
          <Route path="/cards" exact>
            <Private roles={[AccountRole.Admin]}>
              <Cards />
            </Private>
          </Route>
          <Route path="/events" exact>
            <Private roles={[AccountRole.Admin]}>
              <EventsPage />
            </Private>
          </Route>
          <Route path="/event/:id" exact>
            <Private
              roles={[
                AccountRole.Admin,
                AccountRole.Doctor,
                AccountRole.MedicalSpecialist,
              ]}
            >
              <EventPage />
            </Private>
          </Route>

          <Route path="/article/:id" exact>
            <Private roles={[AccountRole.Admin, AccountRole.Author]}>
              <ArticleEditPage />
            </Private>
          </Route>

          <Route path="/articles/create" exact>
            <Private roles={[AccountRole.Admin, AccountRole.Author]}>
              <CreateArticlePage />
            </Private>
          </Route>

          <Route path="/hosted-events" exact>
            <Private
              roles={[AccountRole.Doctor, AccountRole.MedicalSpecialist]}
            >
              <MyEventsPage />
            </Private>
          </Route>
          <Route path="/tiers" exact>
            <Private roles={[AccountRole.Admin]}>
              <TierList />
            </Private>
          </Route>
          <Route path="/tier/:id" exact roles={[AccountRole.Admin]}>
            <Private roles={[AccountRole.Admin]}>
              <Tier />
            </Private>
          </Route>
          <Route path="/requests" exact roles={[AccountRole.Admin]}>
            <Private roles={[AccountRole.Admin]}>
              <DoctorRequests />
            </Private>
          </Route>
          <Route path="/request/:id" exact roles={[AccountRole.Admin]}>
            <Private roles={[AccountRole.Admin]}>
              <DoctorRequest />
            </Private>
          </Route>
        </DashboardLayout>
      </Switch>
    </Router>
  );
}
